import { ISelectItem } from "@components/inputs/select/Select.types";
import { addEmptyLineItem } from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import BindingContext from "@odata/BindingContext";
import { getExpandedEnumProps } from "@odata/Data.utils";
import {
    EntitySetName,
    EntityTypeName,
    IPrEmploymentCreateDelayedIncomeParameters,
    IPrEmploymentEntity,
    IPrPayslipEmploymentSalaryComponentEntity,
    IPrPayslipEntity,
    OdataActionName,
    PrEmploymentEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEmploymentSalaryComponentEntity,
    PrPayslipEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrEmploymentTypeCode,
    PrSalaryComponentAverageInclusionCode,
    PrSalaryComponentTypeCode,
    PrTimeBasedBonusTypeCode
} from "@odata/GeneratedEnums";
import { parseError } from "@odata/ODataParser";
import { transformUserSelectItems, universalPersonNameFormatter } from "@pages/admin/users/Users.utils";
import { getClosedEmployments } from "@pages/payroll/employee/Employee.utils";
import { EmploymentPath } from "@pages/payroll/payslip/delayedIncome/DelayedIncome.def";
import { isDefined } from "@utils/general";
import { cloneDeep } from "lodash";
import React from "react";

import {
    WithPermissionContext,
    withPermissionContext
} from "../../../../contexts/permissionContext/withPermissionContext";
import { Status } from "../../../../enums";
import { AwaitedArray } from "../../../../global.types";
import { IDialogFormViewProps } from "../../../../views/formView/DialogFormView";
import { getAlertFromError } from "../../../../views/formView/Form.utils";
import {
    IContextInitArgs,
    IFormStorageDefaultCustomData,
    IFormStorageSaveResult
} from "../../../../views/formView/FormStorage";
import { FormViewForExtend } from "../../../../views/formView/FormView";

export interface IDelayedIncomeCustomData extends IFormStorageDefaultCustomData {
    employeeSelectItems?: ISelectItem[];
    employmentSelectItems?: ISelectItem[];
}

interface IProps extends IDialogFormViewProps<IPrPayslipEntity, IDelayedIncomeCustomData>, WithPermissionContext {
    year?: number;
    month?: number;
}

const FAKE_EMPLOYMENT_ID = 0;

class DelayedIncomeFormView extends FormViewForExtend<IPrPayslipEntity, IProps> {
    getAdditionalLoadPromise(args: IContextInitArgs): (Promise<unknown | void>)[] {
        const { storage } = this.props;
        return [
            getClosedEmployments(storage.oData, this.props.year, this.props.month),
            ...super.getAdditionalLoadPromise(args)
        ];
    }

    get isWithoutEndedEmployments(): boolean {
        return (this.getAdditionalResults()[0] as IPrEmploymentEntity[])?.length === 0;
    }

    createSelectItems = (): void => {
        const { storage } = this.props;
        const bc = storage.data.bindingContext;

        const employments = this.getAdditionalResults()[0] as IPrEmploymentEntity[];

        const employeeSelectItems = employments.reduce((acc: ISelectItem[], employment) => {
            const employee = employment.Employee;
            if (acc.find(e => e.id === employee.Id)) {
                return acc;
            }
            acc.push({
                id: employee.Id,
                label: universalPersonNameFormatter(employee),
                additionalData: employee,
                tabularData: [universalPersonNameFormatter(employee), employee.NumberOurs]
            });
            return acc;
        }, []);

        storage.getInfo(bc.navigate(PrPayslipEntity.Employee)).fieldSettings.items = transformUserSelectItems(employeeSelectItems);

        const employmentSelectItems = employments.map(employment => ({
            id: employment.Id,
            label: employment.NumberOurs,
            additionalData: employment,
            tabularData: [employment.NumberOurs, employment.Type.Name]
        }));

        storage.setCustomData({
            employeeSelectItems,
            employmentSelectItems
        });
    };

    setDefaultItems = (): void => {
        const { storage } = this.props;
        const itemsGroup = storage.data.definition.groups.find(group => group.id === "SalaryComponents");
        const emptySalaryComponent: IPrPayslipEmploymentSalaryComponentEntity = addEmptyLineItem({
            storage,
            bindingContext: storage.data.bindingContext.navigate(PrPayslipEntity.Employments).addKey(FAKE_EMPLOYMENT_ID).navigate(PrPayslipEmploymentEntity.SalaryComponents),
            newItemsCount: 1,
            columns: itemsGroup.lineItems.columns,
            context: storage.context
        });

        storage.setValueByPath(PrPayslipEntity.Employments, [{
            Id: FAKE_EMPLOYMENT_ID,
            Employment: {
                ...getExpandedEnumProps(EntityTypeName.PrEmploymentType, PrEmploymentTypeCode.Compensation, PrEmploymentEntity.Type)
            },
            SalaryComponents: [emptySalaryComponent]
        }]);
    };

    getAdditionalResults(): AwaitedArray<ReturnType<DelayedIncomeFormView["getAdditionalLoadPromise"]>> {
        return this.props.storage.data.additionalResults;
    }

    async onAfterLoad(): Promise<void> {
        this.createSelectItems();
        this.setDefaultItems();

        if (this.isWithoutEndedEmployments) {
            this.props.storage.setFormAlert({
                status: Status.Warning,
                title: this.props.storage.t("Payslip:DelayedIncome.NotFoundAnyEndedEmployment"),
                isFullWidth: true,
                isOneLiner: true
            });
        }

        return super.onAfterLoad();
    }

    async handleChange(args: ISmartFieldChange): Promise<void> {
        if (args.bindingContext.getPath() === PrPayslipEntity.Employee && args.triggerAdditionalTasks) {
            const storage = this.props.storage;
            const employmentBc = storage.data.bindingContext.navigate(EmploymentPath);
            const employmentInfo = storage.getInfo(employmentBc);
            const employmentSelectItems = storage.getCustomData().employmentSelectItems;
            let employeeItems: ISelectItem[] = [];
            const employeeId = args.value;
            if (employeeId) {
                employeeItems = employmentSelectItems.filter(item => item.additionalData.Employee.Id === employeeId);
            }
            if (employeeItems.length === 1) {
                storage.setValueByPath(EmploymentPath, employeeItems[0].id);
                employmentInfo.isDisabled = true;
            } else {
                storage.clearValueByPath(EmploymentPath);
                employmentInfo.isDisabled = false;
            }
        }

        return super.handleChange(args);
    }

    handleLineItemsChange(args: ISmartFieldChange): void {
        this.props.storage.handleLineItemsChange(args);
        if (args.bindingContext.getPath() === PrPayslipEmploymentSalaryComponentEntity.Type && args.triggerAdditionalTasks) {
            this.props.storage.refresh();
        } else {
            this.props.storage.refreshFields();
        }
    }

    renderButtons(): React.ReactElement {
        return null;
    }

    save = async (): Promise<IFormStorageSaveResult> => {
        const { storage } = this.props;
        const validationRes = await storage.validateAll();
        if (!validationRes.hasError) {
            const data = storage.clearHiddenFields();
            const employmentId = storage.getValueByPath(EmploymentPath);
            const params: IPrEmploymentCreateDelayedIncomeParameters = {
                Year: this.props.year,
                Month: this.props.month,
                Employee: data.Employee,
                SalaryComponents: data.Employments[0].SalaryComponents?.map(sc => {
                    const copy = cloneDeep(sc);
                    delete copy[BindingContext.NEW_ENTITY_ID_PROP];
                    // todo: BE requires Name for each type, this is a temporary solution
                    for (const [key, val] of Object.entries(copy)) {
                        if (isDefined(val?.Code)) {
                            // @ts-ignore
                            copy[key + "Code"] = val.Code;
                            // @ts-ignore
                            delete copy[key];
                        }
                    }

                    if (copy.TypeCode === PrSalaryComponentTypeCode.TimeBasedBonus) {
                        copy.TimeBasedBonusTypeCode = PrTimeBasedBonusTypeCode.Monthly;
                        copy.AverageInclusionCode = PrSalaryComponentAverageInclusionCode.Exclude;
                    }
                    copy.IsOneTime = true;
                    return copy;
                })
            };
            const wrapper = storage.oData.getEntitySetWrapper(EntitySetName.PrEmployments);
            try {
                await wrapper.action(OdataActionName.PrEmploymentCreateDelayedIncome, employmentId, params);
                this.props.onAfterConfirm({});
            } catch (e) {
                const error = parseError(e);
                const errorAlert = getAlertFromError(error);
                storage.setFormAlert(errorAlert);
                storage.refresh();
            }
        } else {
            storage.refresh();
        }

        return null;
    };
}

export default withPermissionContext(DelayedIncomeFormView);